import React from 'react';
import { Plus, Trash2, Image, GripVertical } from 'lucide-react';
import ColorEditor from '../ColorEditor';
import { resolveImageUrl } from '../../utils/imageUtils';

const GalleryEditor = ({ content, onContentChange, onArrayFieldChange, onAddArrayItem, onRemoveArrayItem, uploading, onImageUpload, onOpenImagePicker, site, onCreateCustomTheme }) => {
  return (
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Section Title
        </label>
        <input
          type="text"
          value={content.title || ''}
          onChange={(e) => onContentChange('title', e.target.value)}
          className="w-full text-xs px-2 py-1 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          placeholder="Our Gallery"
        />
      </div>

      {/* Gallery Elements */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-3">
          Gallery Description
        </label>
        <textarea
          value={content.description || ''}
          onChange={(e) => onContentChange('description', e.target.value)}
          className="w-full text-xs px-2 py-1 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          placeholder="Optional description for your gallery"
          rows="4"
        />
      </div>

      {/* Images List */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-3">Images</label>
        <div className="space-y-3">
          {(content.images || []).map((image, index) => (
            <div key={index} className="border border-gray-200 rounded p-1">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center">
                  <GripVertical className="w-4 h-4 text-gray-400 mr-2" />
                  <span className="text-sm font-medium text-gray-700">Image {index + 1}</span>
                </div>
                <button
                  onClick={() => onRemoveArrayItem('images', index)}
                  className="text-gray-400 hover:text-red-500"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
              
              {/* Image Upload */}
              <div className="mb-1">
                <div className="flex gap-1">
                  <div 
                    className="flex-1 h-28 bg-gray-100 flex items-center justify-center cursor-pointer hover:bg-gray-200 transition-colors relative overflow-hidden"
                    onClick={() => document.getElementById(`galleryImageInput-${index}`).click()}
                  >
                    {image.url ? (
                      <img 
                        src={resolveImageUrl(image.url)} 
                        alt={image.caption || `Gallery image ${index + 1}`}
                        className="w-full h-full object-cover rounded"
                      />
                    ) : (
                      <div className="text-center">
                        <Image className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                        <div className="text-sm text-gray-500">
                          {uploading ? 'Uploading...' : 'Upload'}
                        </div>
                      </div>
                    )}
                    <input
                      id={`galleryImageInput-${index}`}
                      type="file"
                      accept="image/*"
                      onChange={(e) => onImageUpload(e.target.files[0], `images.${index}.url`)}
                      className="hidden"
                      disabled={uploading}
                    />
                  </div>
                  {onOpenImagePicker && (
                    <button
                      onClick={() => onOpenImagePicker(`images.${index}.url`)}
                      className="px-2 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 transition min-w-0 flex flex-col items-center justify-center"
                      disabled={uploading}
                    >
                      <Image className="w-3 h-3 mb-1" />
                      <span>Picker</span>
                    </button>
                  )}
                </div>
                
                {/* Photo Credit for Gallery Image */}
                {image.urlCredit && (
                  <div className="text-xs text-gray-500 mt-1">
                    Photo by{' '}
                    <a 
                      href={image.urlCredit.photographerUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:underline"
                    >
                      {image.urlCredit.photographer}
                    </a>
                    {' '}on{' '}
                    {image.urlCredit.provider === 'unsplash' ? (
                      <a 
                        href={image.urlCredit.unsplashUrl || 'https://unsplash.com'}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:underline"
                      >
                        Unsplash
                      </a>
                    ) : (
                      <span>{image.urlCredit.provider || 'Unsplash'}</span>
                    )}
                  </div>
                )}
              </div>
              
              {/* Image Caption */}
              <input
                type="text"
                value={image.caption || ''}
                onChange={(e) => onArrayFieldChange('images', index, { ...image, caption: e.target.value })}
                className="w-full text-xs px-2 py-1 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Image caption (optional)"
              />
            </div>
          ))}
          
          <button
            onClick={() => onAddArrayItem('images', { url: '', caption: '' })}
            className="flex items-center text-sm text-blue-600 hover:text-blue-800"
          >
            <Plus className="w-4 h-4 mr-1" />
            Add image
          </button>
          
          <div className="text-xs text-gray-500">
            You can upload up to 12 images. Supported formats: JPG, PNG, WebP
          </div>
        </div>
      </div>

      {/* Layout Options */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-3">Layout</label>
        <div className="space-y-2">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Layout Style</label>
            <select
              value={content.layout || 'uniform-grid'}
              onChange={(e) => onContentChange('layout', e.target.value)}
              className="w-full text-xs px-2 py-1 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="uniform-grid">Uniform Grid</option>
              <option value="masonry-grid">Masonry Grid</option>
              <option value="carousel">Carousel</option>
              <option value="lightbox">Lightbox Preview</option>
            </select>
          </div>
          
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Image Frame</label>
            <select
              value={content.frame || 'landscape'}
              onChange={(e) => onContentChange('frame', e.target.value)}
              className="w-full text-xs px-2 py-1 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="landscape">Landscape (4:3)</option>
              <option value="portrait">Portrait (3:4)</option>
              <option value="square">Square (1:1)</option>
            </select>
          </div>
          
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Image Borders</label>
            <select
              value={content.borders || 'none'}
              onChange={(e) => onContentChange('borders', e.target.value)}
              className="w-full text-xs px-2 py-1 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="none">None</option>
              <option value="all">All Borders</option>
              <option value="bottom">Bottom Border</option>
            </select>
          </div>
        </div>
      </div>


      {/* Color Theme - Always at bottom */}
      <ColorEditor
        value={content.colorTheme || 'default'}
        onChange={(value) => onContentChange('colorTheme', value === 'default' ? null : value)}
        label="Section Color Theme"
        showReset={true}
        resetValue="default"
        siteTheme={site?.design?.colorTheme ? {
          name: site.design.colorThemeName || 'Site Theme',
          colors: site.design.colorTheme
        } : null}
        customThemes={site?.design?.customThemes || []}
        onCreateCustomTheme={onCreateCustomTheme}
      />
    </div>
  );
};

export default GalleryEditor;